/**
 * Hook: useWahaMonitor
 * Gerencia monitoramento de sessões WAHA no painel admin
 * 
 * @version 1.0.0
 * @created 2026-01-05
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// ============================================
// Tipos e Interfaces
// ============================================

/** Status possíveis de uma sessão WAHA */
export type WahaStatus = 'WORKING' | 'STOPPED' | 'STARTING' | 'SCAN_QR_CODE' | 'FAILED';

/** Sessão processada retornada pela Edge Function */
export interface WahaSession {
  sessionName: string;
  wahaStatus: WahaStatus | null;
  localStatus: string | null;
  match: boolean;
  isOrphan: boolean;
  isGhost: boolean;
  phone: string | null;
  pushName: string | null;
  engine: string | null;
  inbox: {
    id: string;
    name: string;
    tenant: string;
    tenantId: string;
  } | null;
  lastValidatedAt: string | null;
}

/** Alerta de inconsistência */
export interface WahaAlert {
  type: 'info' | 'warning' | 'error';
  message: string;
  count: number;
}

/** Resumo dos status */
export interface WahaSummary {
  working: number;
  stopped: number;
  starting: number;
  failed: number;
  orphans: number;
  ghosts: number;
  mismatches: number;
}

/** Dados completos do monitor */
export interface WahaMonitorData {
  success: boolean;
  wahaServer: string;
  totalWahaSessions: number;
  totalLocalInboxes: number;
  summary: WahaSummary;
  sessions: WahaSession[];
  alerts: WahaAlert[];
  verifiedAt: string;
}

/** Resultado de sincronização */
interface SyncResult {
  success: boolean;
  message?: string;
  previousStatus?: string;
  newStatus?: string;
  wahaStatus?: WahaStatus;
  error?: string;
}

/** Resultado de deleção */
interface DeleteResult {
  success: boolean;
  message?: string;
  error?: string;
}

// ============================================
// Funções de API
// ============================================

/**
 * Busca todas as sessões do WAHA e compara com inboxes locais
 */
async function fetchWahaSessions(): Promise<WahaMonitorData> {
  logger.info('[useWahaMonitor]', 'Buscando sessões WAHA...');
  
  const { data, error } = await supabase.functions.invoke<WahaMonitorData>('admin-waha-monitor', {
    body: { action: 'list_sessions' },
  });
  
  if (error) {
    logger.error('[useWahaMonitor]', 'Erro ao buscar sessões:', error);
    throw new Error(error.message || 'Erro ao buscar sessões WAHA');
  }
  
  if (!data?.success) {
    throw new Error((data as { error?: string })?.error || 'Resposta inválida da API');
  }
  
  logger.info('[useWahaMonitor]', 'Sessões recebidas:', JSON.stringify(data.summary));
  return data;
}

/**
 * Sincroniza status de uma sessão específica
 */
async function syncSessionStatus(sessionName: string, inboxId: string): Promise<SyncResult> {
  logger.info('[useWahaMonitor]', `Sincronizando sessão: ${sessionName}`);
  
  const { data, error } = await supabase.functions.invoke<SyncResult>('admin-waha-monitor', {
    body: { action: 'sync_status', sessionName, inboxId },
  });
  
  if (error) {
    logger.error('[useWahaMonitor]', 'Erro ao sincronizar:', error);
    throw new Error(error.message || 'Erro ao sincronizar status');
  }
  
  return data as SyncResult;
}

/**
 * Deleta sessão órfã do WAHA
 */
async function deleteOrphanSession(sessionName: string): Promise<DeleteResult> {
  logger.info('[useWahaMonitor]', `Deletando sessão órfã: ${sessionName}`);
  
  const { data, error } = await supabase.functions.invoke<DeleteResult>('admin-waha-monitor', {
    body: { action: 'delete_orphan', sessionName },
  });
  
  if (error) {
    logger.error('[useWahaMonitor]', 'Erro ao deletar:', error);
    throw new Error(error.message || 'Erro ao deletar sessão');
  }
  
  return data as DeleteResult;
}

// ============================================
// Hook Principal
// ============================================

/**
 * Hook para monitoramento de sessões WAHA
 * @param enabled - Se deve buscar dados automaticamente
 * @param refetchInterval - Intervalo de atualização em ms (0 = desabilitado)
 */
export function useWahaMonitor(enabled = true, refetchInterval = 0) {
  const queryClient = useQueryClient();
  
  // Query principal para listar sessões
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['waha-monitor'],
    queryFn: fetchWahaSessions,
    enabled,
    refetchInterval: refetchInterval > 0 ? refetchInterval : false,
    staleTime: 30000, // 30 segundos
    gcTime: 60000, // 1 minuto (anteriormente cacheTime)
  });
  
  // Mutation para sincronizar status individual
  const syncMutation = useMutation({
    mutationFn: ({ sessionName, inboxId }: { sessionName: string; inboxId: string }) =>
      syncSessionStatus(sessionName, inboxId),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message || 'Status sincronizado com sucesso');
        // Invalida cache para recarregar dados
        queryClient.invalidateQueries({ queryKey: ['waha-monitor'] });
      } else {
        toast.error(result.error || 'Erro ao sincronizar status');
      }
    },
    onError: (error: Error) => {
      toast.error(`Erro ao sincronizar: ${error.message}`);
    },
  });
  
  // Mutation para deletar sessão órfã
  const deleteMutation = useMutation({
    mutationFn: (sessionName: string) => deleteOrphanSession(sessionName),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message || 'Sessão deletada com sucesso');
        queryClient.invalidateQueries({ queryKey: ['waha-monitor'] });
      } else {
        toast.error(result.error || 'Erro ao deletar sessão');
      }
    },
    onError: (error: Error) => {
      toast.error(`Erro ao deletar: ${error.message}`);
    },
  });
  
  return {
    // Dados
    data,
    sessions: data?.sessions || [],
    summary: data?.summary || {
      working: 0,
      stopped: 0,
      starting: 0,
      failed: 0,
      orphans: 0,
      ghosts: 0,
      mismatches: 0,
    },
    alerts: data?.alerts || [],
    wahaServer: data?.wahaServer || null,
    verifiedAt: data?.verifiedAt || null,
    
    // Estados
    isLoading,
    isError,
    error,
    isFetching,
    
    // Ações
    refetch,
    syncStatus: syncMutation.mutate,
    isSyncing: syncMutation.isPending,
    deleteOrphan: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
}
